import React from 'react';
import { cn } from '../../lib/utils';
import { PrimaryButton } from './PrimaryButton';
import { SecondaryButton } from './SecondaryButton';

interface AutoResizeButtonProps {
  variant: 'primary' | 'secondary';
  icon?: React.ReactNode;
  text: string;
  shortText?: string;
  className?: string;
  size?: 'default' | 'sm' | 'lg' | 'profile';
  onClick?: () => void;
  disabled?: boolean;
}

const AutoResizeButton: React.FC<AutoResizeButtonProps> = ({
  variant,
  icon,
  text,
  shortText,
  className,
  size = 'profile',
  onClick,
  disabled = false
}) => {
  // Use fixed font size to prevent infinite resize loops
  const fontSize = 12;
  const ref = React.useRef<HTMLElement>(null);
  
  // Use container width to determine text display
  const [useShortText, setUseShortText] = React.useState(false);
  
  React.useEffect(() => {
    const checkWidth = () => {
      if (ref.current && ref.current.parentElement) {
        const containerWidth = ref.current.parentElement.clientWidth;
        // Use short text only on very small mobile screens (less than 120px)
        setUseShortText(containerWidth < 120 && !!shortText);
      }
    };
    
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, [shortText]);

  // Determine which text to show based on container width
  const displayText = useShortText ? shortText : text;

  // Fixed icon size
  const iconSize = 'w-4 h-4';

  const buttonClassName = cn(
    'flex-1 min-w-0 transition-all duration-200',
    className
  );

  const content = (
    <>
      {icon && (
        <span className={cn('flex-shrink-0', iconSize)}>
          {React.cloneElement(icon as React.ReactElement, {
            className: cn(
              iconSize,
              'flex-shrink-0',
              variant === 'primary' ? 'text-white' : 'text-current'
            )
          } as any)}
        </span>
      )}
      <span 
        ref={ref}
        className={cn(
          "truncate font-medium transition-all duration-200",
          variant === 'primary' ? 'text-white' : 'text-current'
        )}
        style={{ fontSize: `${fontSize}px` }}
      >
        {displayText}
      </span>
    </>
  );

  if (variant === 'primary') {
    return (
      <PrimaryButton 
        size={size}
        className={buttonClassName}
        onClick={onClick}
        disabled={disabled}
      >
        {content}
      </PrimaryButton>
    );
  }

  return (
    <SecondaryButton 
      size={size}
      className={buttonClassName}
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </SecondaryButton>
  );
};

export default AutoResizeButton;